import { WidgetsContainerAbstract } from "src/app/abstract/widgets/widgets-container.abstract";
import { WidgetsTypeEnum } from "src/app/enums/widgets/widgets-type.enum";
import { WidgetsAbstract } from "../../abstract/widgets/widgets.abstract";

export class WidgetsQuestionModel extends WidgetsContainerAbstract {
    // Automatically populated when created with standard attributes
    readonly widgetType: WidgetsTypeEnum = WidgetsTypeEnum.QUESTION;
    readonly badge: string = 'Q';
    readonly placeholderClassName: string = 'question-placeholder';
    readonly icon: string = 'widgets';

    /**
     * Formly needs a unique key for each field. It's better human-friendly (id / uuid is not).
     *
     * @remarks
     *
     * - For fields this is the key that will use in rendered forms for the model ('model.key')
     * - For questions/groups this defines the namespace for the fields that live inside ('model.groupkey.key').
     *
     * @author mgsg
     */
    key: string = '';

    // Design properties
    name: string = 'Question';
    description?: string;
    label?: string;
    seq: number = 0;

    labelOverrides: Map<string, string> = new Map<string, string>();
    tags: string[] = ["question"];
    template = '';


    // DB data
    /**
     * id in database for this question. If undefined, the question is not in the DB.
     * It will be inferred that it's a local question.
     */
    dbId?: number;
    typeId?: number;        // Custom, Primitive,...
    createdBy?: string;
    created: string = '';       // DB: Stored as "2022-08-15T16:35:36.120Z"

    // eslint-disable-next-line
    TypeName?: string = '';
    // DB data end

    /**
     * Comment required
     */
    isCommentRequired?: boolean;
    commentTrigger?: string;

    /**
     * Smart Form Question Description
     */
    smartFormDescription?: string;

    /**
     * Marking form question for review
     */
    isMarkedForReview?: boolean;
    /**
     * Is Risk Form Question
     */
    isPresentInRiskForm?: boolean;
    /**
 * Risk Form Question Description
 */
    riskFormDescription?: string;

    /**
   * For table question
   */
    isTable?: boolean = false;

    /**
   * For OnPoint question - mark questions for OnPoint experience
   */
    isOnPoint?: boolean = false;

    /**
       * For complex question
       */
    isComplex?: boolean = false;


    /**
     * full key name (including namespaces) of a field that this question's visibility state depends on
     */
    hideField: string | undefined = undefined;
    children: WidgetsAbstract[] = [];

    constructor() {
        super();
        this.id += this.badge;
    }

    /**
     * Return a JSON object that represent an instance of this model class
     *
     * @note An undefined attribute won't be part of the JSON: good to save space!
     * @note For legibility, badge and widgettype at the top, arrays at the bottom
     * @note Changes in this method will have an impact in the backend API/DB. It has been modelled to contain this properties.
     * @returns JSON with question model representation
     */
    toJSON(): any {
        return {
            // Automatically populated: no need to clone
            badge: this.badge,                                          // We keep it here for legibility
            widgetType: this.widgetType,
            id: this.id,                                                // Do we really need to write the id?
            parentId: this.parentId,                                    // Do we really need to write the parentId?

            // Base data
            name: this.name,
            description: this.description ? this.description : undefined,
            key: this.key,
            seq: this.seq,

            // DB data
            dbId: this.dbId ? this.dbId : undefined,
            // eslint-disable-next-line
            TypeName: this.TypeName ? this.TypeName : undefined,        // TODO: Remove capital letter
            typeId: this.typeId ? this.typeId : undefined,
            createdBy: this.createdBy ? this.createdBy : undefined,
            created: this.created ? this.created : undefined,
            // DB data end

            // Additional data
            isCommentRequired: this.isCommentRequired ? this.isCommentRequired : undefined,
            commentTrigger: this.commentTrigger ? this.commentTrigger : undefined,
            smartFormDescription: this.smartFormDescription ? this.smartFormDescription : undefined,
            isPresentInRiskForm: this.isPresentInRiskForm ? this.isPresentInRiskForm : undefined,
            riskFormDescription: this.riskFormDescription ? this.riskFormDescription : undefined,
            isMarkedForReview: this.isMarkedForReview ? this.isMarkedForReview : undefined,
            isTable: this.isTable ? this.isTable : false,
            isOnPoint: this.isOnPoint ? this.isOnPoint : false,

            // Long attributes at the end for JSON legibility
            hideField: this.hideField ? this.hideField : undefined,
            template: this.template ? this.template : undefined,
            labelOverrides: this.labelOverrides ? Object.fromEntries(this.labelOverrides) : undefined,
            tags: this.tags,
            children: this.children
        };
    }

    /**
     * Clone this node from an origin.
     *
     * @warning children, id and parentId are not cloned.
     * @param origin
     * @returns the cloned node
     */
    cloneFrom(origin: WidgetsQuestionModel): WidgetsQuestionModel {
        // Id and parentId not cloned
        // this.id = origin.id;
        // this.parentId = origin.parentId;
        // this.isSelected = origin.isSelected;

        // Base data
        this.name = origin.name;
        if (origin.description) {
            this.description = origin.description;
        }
        this.key = origin.key;
        this.seq = origin.seq;

        // DB data
        if (origin.dbId) {
            this.dbId = origin.dbId;
        }
        if (origin.typeId) {
            this.typeId = origin.typeId;
        }
        if (origin.TypeName) {
            this.TypeName = origin.TypeName;
        }
        if (origin.created) {
            this.created = origin.created;
        }
        if (origin.createdBy) {
            this.createdBy = origin.createdBy;
        }
        // DB data end

        // Additional data
        this.hideField = origin.hideField;
        if (origin.template) {
            this.template = origin.template;
        }
        if (origin.isCommentRequired) {
            this.isCommentRequired = origin.isCommentRequired;
        }
        if (origin.commentTrigger) {
            this.commentTrigger = origin.commentTrigger;
        }
        if (origin.smartFormDescription) {
            this.smartFormDescription = origin.smartFormDescription;
        }
        if (origin.isPresentInRiskForm) {
            this.isPresentInRiskForm = origin.isPresentInRiskForm;
            this.riskFormDescription = origin.riskFormDescription;
        }
        if (origin.isMarkedForReview) {
            this.isMarkedForReview = origin.isMarkedForReview;
        }
        if (origin.isTable) {
            this.isTable = origin.isTable;
        }
        if (origin.isOnPoint) {
            this.isOnPoint = origin.isOnPoint;
        }


        // Arrays/Maps
        if (origin.labelOverrides && origin.labelOverrides.size > 0) {
            this.labelOverrides = new Map<string, string>(origin.labelOverrides);
        }
        // Tags: deep clone?
        this.tags = [];
        if (origin.tags) {
            origin.tags.forEach((tag) => {
                this.tags.push(tag);
            });
        }
        // children not cloned
        return this;
    }

    hasSequence(): boolean {
        return true;
    }

    isPopulated(): boolean {
        if (this.IsPureFormlyQues()) { return true };
        if (this.children && this.children.length > 0) {
            return true;
        }
        return false;
    }

    /**
     * It will return true, if the question is pure formly question.
     * Pure formly question does not have children.
     * If a question @template contain @fieldGroup object than it is a pure Formly question 
     * **/
    IsPureFormlyQues() {
        if (this.template) {
            const parseJsonTemplate = JSON.parse(this.template);
            const isPureFormlyQues = parseJsonTemplate?.fieldGroup;

            if (isPureFormlyQues) {
                return true;
            } else {
                return false
            }
        }
    }
}
